import {
  ApiError, createApiKey, listApiKeys, listApiKeyScopes, revokeApiKey, type ApiKey,
} from "@/api";
import { Button } from "@/components/ui/Button";
import { type FormEvent, useEffect, useState } from "react";

const LABELS: Record<string, string> = {
  "monitoring.read": "Status und Versandstatistik lesen",
  "event.read": "Meldungen mit Mailinhalten lesen",
  "event.write": "Meldungen und Entwürfe bearbeiten",
  "message.read": "Mailinhalte und Empfänger lesen",
  "message.send": "Mails versenden",
  "message.schedule": "Versand zeitlich planen",
  "template.read": "Vorlagen lesen",
  "template.write": "Vorlagen bearbeiten",
  "sender.read": "Absender lesen",
  "audience.read": "Zielgruppen und Quellen lesen",
  "audience.sync": "Empfängerdaten synchronisieren",
  "archive.read": "Mailarchiv lesen",
  "settings.write": "Absender und Quellen konfigurieren",
  "audit.read": "Audit-Log lesen",
  "bounce.read": "Rückläufer und Adressvermerke lesen",
  "bounce.write": "Adressvermerke zurücksetzen",
};

const AUTHORING = [
  "monitoring.read", "event.read", "event.write", "message.read",
  "template.read", "template.write", "sender.read", "audience.read",
];

function dateLabel(value: string): string {
  return new Date(value).toLocaleString("de-DE");
}

export function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>(["monitoring.read"]);
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [created, setCreated] = useState<(ApiKey & { key: string }) | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([listApiKeys(), listApiKeyScopes()])
      .then(([items, options]) => {
        if (active) {
          setKeys(items);
          setAvailableScopes(options);
        }
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : "Schlüssel konnten nicht geladen werden.");
      });
    return () => { active = false; };
  }, []);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await createApiKey({
        name: name.trim(), scopes,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      setCreated(result);
      // Keep the secret only in the one-time display, never in the key inventory.
      const { key: _secret, ...metadata } = result;
      setKeys((previous) => [metadata, ...(previous ?? [])]);
      setName("");
      setCopied(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Schlüssel konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(id: string) {
    setBusy(true);
    setError(null);
    try {
      await revokeApiKey(id);
      setKeys((previous) => previous?.map((key) => (
        key.id === id ? { ...key, revoked_at: new Date().toISOString() } : key
      )) ?? null);
      if (created?.id === id) setCreated(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Schlüssel konnte nicht widerrufen werden.");
    } finally {
      setBusy(false);
    }
  }

  async function copyKey() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
    } catch {
      setError("Bitte den Schlüssel im Textfeld markieren und kopieren.");
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-4">
      <div>
        <h1 className="text-base font-semibold">API-Schlüssel</h1>
        <p className="mt-1 text-sm text-muted">
          Eigene Schlüssel für Automatisierung, Monitoring und MCP. Jeder Schlüssel nutzt
          dein Benutzerkonto mit den ausgewählten Rechten.
        </p>
      </div>
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
      {created ? (
        <section className="space-y-3 rounded-lg border border-accent bg-surface p-4">
          <h2 className="text-sm font-semibold">Schlüssel „{created.name}“ erstellt</h2>
          <p className="text-sm text-muted">Jetzt sicher speichern. Der vollständige Schlüssel wird nur einmal angezeigt.</p>
          <label className="field-label" htmlFor="created-key">API-Schlüssel</label>
          <input id="created-key" className="field font-mono text-xs" readOnly value={created.key}
            onFocus={(event) => event.target.select()} autoComplete="off" spellCheck={false} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void copyKey()}>{copied ? "Kopiert" : "Kopieren"}</Button>
            <Button variant="ghost" onClick={() => setCreated(null)}>Schlüssel gespeichert · Schließen</Button>
          </div>
        </section>
      ) : (
        <form onSubmit={(event) => void onCreate(event)} className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
          <h2 className="text-sm font-semibold">Neuer Schlüssel</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="key-name">Name</label>
              <input id="key-name" className="field" required maxLength={255} placeholder="z. B. Monitoring"
                value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="key-expiry">Gültig bis (optional, lokale Zeit)</label>
              <input id="key-expiry" className="field" type="datetime-local" value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)} />
            </div>
          </div>
          <fieldset disabled={busy}>
            <legend className="field-label">Berechtigungen</legend>
            <div className="mb-2 flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={() => setScopes(["monitoring.read"])}>Monitoring</Button>
              <Button type="button" size="sm" onClick={() => setScopes(AUTHORING)}>Vorlagen und Entwürfe</Button>
              <Button type="button" size="sm" onClick={() => setScopes([...AUTHORING, "message.send", "message.schedule"])}>Mit Versand</Button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {availableScopes.map((scope) => (
                <label key={scope} className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" checked={scopes.includes(scope)}
                    onChange={(event) => setScopes((previous) => event.target.checked
                      ? [...previous, scope] : previous.filter((item) => item !== scope))} />
                  <span>{LABELS[scope] ?? scope}<span className="block font-mono text-[11px] text-muted">{scope}</span></span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="flex justify-end">
            <Button type="submit" variant="primary" disabled={busy || !name.trim() || scopes.length === 0 || !keys}>
              {busy ? "Bitte warten…" : "Schlüssel erstellen"}
            </Button>
          </div>
        </form>
      )}
      <section>
        <h2 className="mb-2 text-sm font-semibold">Meine Schlüssel</h2>
        {keys === null && !error ? <p className="text-sm text-muted">Schlüssel werden geladen…</p> : null}
        {keys?.length === 0 ? <p className="text-sm text-muted">Noch keine Schlüssel angelegt.</p> : null}
        <ul className="divide-y divide-hairline rounded-lg border border-hairline bg-surface">
          {keys?.map((key) => {
            const expired = key.expires_at !== null && new Date(key.expires_at).getTime() <= Date.now();
            return (
              <li key={key.id} className="flex items-start gap-3 p-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-medium">{key.name} · {key.revoked_at ? "Widerrufen" : expired ? "Abgelaufen" : "Aktiv"}</p>
                  <p className="font-mono text-xs text-muted">{key.key_prefix}…</p>
                  <p className="break-words text-xs text-muted">{key.scopes.join(", ")}</p>
                  <p className="text-xs text-muted">Erstellt {dateLabel(key.created_at)} · {key.expires_at ? `Gültig bis ${dateLabel(key.expires_at)}` : "Ohne Ablaufdatum"}</p>
                </div>
                {!key.revoked_at ? (
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => void onRevoke(key.id)}>Widerrufen</Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
      <section className="space-y-2 text-sm text-muted">
        <h2 className="font-semibold text-ink">API und MCP verbinden</h2>
        <p>REST-API: <code>/api/v1</code> · MCP (Streamable HTTP): <code>{window.location.origin}/mcp</code></p>
        <p>Header: <code>Authorization: Bearer &lt;API-Schlüssel&gt;</code> oder <code>X-API-Key</code>.</p>
        <p>Status: <code>/api/v1/status</code> sowie <code>/api/v1/messages/&#123;id&#125;/status</code>.</p>
        <a className="text-accent underline" href="/docs" target="_blank" rel="noreferrer">API-Dokumentation öffnen</a>
      </section>
    </div>
  );
}
